/**
 * Список роликов: собранные в этом окне и прошлые — из истории `GET /api/video/clips` (окно можно
 * закрыть, пока ролик собирается: он доберётся на сервере и появится здесь готовым). У готового —
 * «Показать» (плеер под списком), «Скачать», «Отправить клиенту» с общей подписью, если открыли из
 * чата. Общий для окна видео и блока «Видео» вкладки «Договор». Спека `docs/specs/2026-10-23-video-design.md`.
 */
import { Download, LoaderCircle, Play, RotateCw, Send } from "lucide-react";
import { useEffect, useState } from "react";

import { clipUrl, clock, dur, secOf, sendClip, shortDay } from "@/lib/video";

export type ClipItem = {
  id: string;
  camera: string;
  from: string;
  to: string;
  status: "queued" | "running" | "ready" | "error" | "gone";
  key?: string | undefined;
  size?: number | undefined;
  error?: string | undefined;
  startedAt?: number | undefined;
  user?: string | undefined;
  /** Собрать заново (ошибка или ролик пропал после рестарта сервиса). */
  retry?: (() => void) | undefined;
};

const btn = "inline-flex h-7 items-center gap-1 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12px] hover:bg-bg-subtle disabled:opacity-50";
const primary = "inline-flex h-7 items-center gap-1 rounded-[var(--radius-ui)] bg-accent px-2.5 text-[12px] font-medium text-white hover:opacity-90 disabled:opacity-50";

export function ClipList({
  items,
  chatId = null,
  dealId = null,
  caption = "",
  show = null,
  empty = null,
}: {
  items: ClipItem[];
  chatId?: number | null;
  dealId?: number | null;
  caption?: string;
  /** Ключ ролика, который только что собрался в этом окне, — его сразу в плеер. */
  show?: string | null;
  empty?: string | null;
}) {
  const [playing, setPlaying] = useState<string | null>(show);
  const [text, setText] = useState(caption);
  const [sent, setSent] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  useEffect(() => {
    if (show) setPlaying(show);
  }, [show]);
  const [, tick] = useState(0);
  const counting = items.some((i) => i.status === "running" && i.startedAt);
  useEffect(() => {
    if (!counting) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [counting]);

  if (!items.length) return empty ? <div className="text-[12px] text-fg-muted">{empty}</div> : null;
  const many = new Set(items.map((i) => i.camera)).size > 1;
  const send = async (i: ClipItem) => {
    if (!i.key || chatId == null) return;
    setSent((s) => ({ ...s, [i.key as string]: "sending" }));
    try {
      const r = await sendClip(i.key, chatId, many ? `${text} · ${i.camera}` : text, dealId);
      setSent((s) => ({ ...s, [i.key as string]: "sent" }));
      // Ролик ушёл, подпись нет — повторять ролик нельзя (задвоится), подпись — отдельным сообщением.
      if (r.warning) setNotes((n) => ({ ...n, [i.key as string]: r.warning as string }));
    } catch (e) {
      setSent((s) => ({ ...s, [i.key as string]: (e as Error).message || "ошибка" }));
    }
  };
  const ready = items.filter((i) => i.status === "ready" && i.key);
  const unsent = ready.filter((i) => !sent[i.key as string] || !["sent", "sending"].includes(sent[i.key as string] as string));
  const player = ready.find((i) => i.key === playing);

  return (
    <div className="flex flex-col gap-2" data-annot="ролики">
      {chatId != null && ready.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="подпись к видео для клиента" className="h-7 min-w-0 flex-1 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12px]" />
          {unsent.length >= 2 && (
            <button
              type="button"
              className={primary}
              onClick={async () => {
                for (const i of unsent) await send(i);
              }}
            >
              <Send size={13} /> Отправить все готовые ({unsent.length})
            </button>
          )}
        </div>
      )}
      <div className="flex max-h-[260px] flex-col divide-y divide-border overflow-y-auto rounded border border-border bg-bg">
        {items.map((i) => {
          const s = i.key ? sent[i.key] : undefined;
          return (
            <div key={i.id} className={`flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5 text-[12px] ${i.key && i.key === playing ? "bg-accent/5" : ""}`}>
              <span className="min-w-[130px] font-medium">{i.camera}</span>
              <span className="tabular-nums text-fg-muted">
                {shortDay(i.from)} {clock(secOf(i.from))}–{clock(secOf(i.to))} · {dur(secOf(i.to) - secOf(i.from))}
              </span>
              {i.user && <span className="text-fg-muted">· {i.user}</span>}
              {i.status === "queued" && <span className="text-fg-muted">в очереди</span>}
              {i.status === "running" && (
                <span className="inline-flex items-center gap-1 text-fg-muted">
                  <LoaderCircle size={13} className="animate-spin" />
                  {i.startedAt ? `готовлю… ${Math.round((Date.now() - i.startedAt) / 1000)} с` : "собирается на сервере…"}
                </span>
              )}
              {(i.status === "error" || i.status === "gone") && (
                <>
                  <span className="text-err">{i.status === "gone" ? "не собрался (сервис перезапускали)" : `ошибка: ${i.error}`}</span>
                  {i.retry && (
                    <button type="button" className={btn} onClick={i.retry}>
                      <RotateCw size={12} /> Собрать заново
                    </button>
                  )}
                </>
              )}
              {i.status === "ready" && i.key && (
                <>
                  {i.size != null && <span className="text-fg-muted">{(i.size / 1_048_576).toFixed(1)} МБ</span>}
                  <span className="ml-auto flex flex-wrap items-center gap-1.5">
                    <button type="button" className={`${btn} ${playing === i.key ? "border-accent text-accent-text" : ""}`} onClick={() => setPlaying(playing === i.key ? null : (i.key as string))}>
                      <Play size={12} /> {playing === i.key ? "скрыть" : "показать"}
                    </button>
                    <a href={clipUrl(i.key, true)} className={btn}>
                      <Download size={12} /> Скачать
                    </a>
                    {chatId != null && (
                      <button type="button" className={primary} disabled={s === "sending" || s === "sent"} onClick={() => send(i)}>
                        <Send size={12} /> {s === "sent" ? "Отправлено" : s === "sending" ? "Отправляю…" : "Отправить клиенту"}
                      </button>
                    )}
                  </span>
                  {s && s !== "sending" && s !== "sent" && <div className="w-full text-[11.5px] text-err">Не отправилось: {s}</div>}
                  {s === "sent" && notes[i.key] && (
                    <div className="w-full text-[11.5px] text-warn">Ролик ушёл, подпись — нет: {notes[i.key]}. Подпись отправьте сообщением в чате.</div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      {player?.key && <video key={player.key} src={clipUrl(player.key)} controls autoPlay preload="metadata" className="max-h-[60vh] w-full rounded bg-black" />}
    </div>
  );
}
